import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Alert,
  Button,
  Box,
  LinearProgress,
  Drawer,
  Stack,
  Divider,
  Typography,
  IconButton,
  Link,
  Tooltip,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import BusinessIcon from "@mui/icons-material/Business";
import { fetchRRData } from "../../../../fetchRRData";
import { fetchData } from "../../../../fetchData";

const pageSize = 10;

const getDeliveryStatusLabel = (deliveryResult?: boolean) => {
  if (deliveryResult === undefined) return "Status indisponível";
  if (deliveryResult) return "Cliente notificado";
  return "Notificação pendente";
};

const getDeliveryStatusColor = (deliveryResult?: boolean) => {
  if (deliveryResult === undefined) return "grey.400";
  if (deliveryResult) return "success.main";
  return "error.main";
};

type DeliveredTaskRowProps = {
  task: any;
  organizationId?: string;
  copiedTaskId: number | null;
  onCopyTaskId: (taskId: number) => void;
  onOpenOrganization: (organizationId: string) => void;
};

const DeliveredTaskRow = ({
  task,
  organizationId,
  copiedTaskId,
  onCopyTaskId,
  onOpenOrganization,
}: DeliveredTaskRowProps) => {
  const isCopied = copiedTaskId === task.id;
  const clientName = task.client_name || "Sem cliente";

  return (
    <Box
      sx={{
        px: 1.5,
        py: 1.25,
        borderRadius: 1,
        border: "1px solid",
        borderColor: "divider",
        borderLeftWidth: 3,
        borderLeftColor: getDeliveryStatusColor(task.delivery_result),
        bgcolor: "background.paper",
      }}
    >
      <Stack spacing={0.75}>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <BusinessIcon
            sx={{
              fontSize: 16,
              mt: 0.25,
              color: organizationId ? "primary.main" : "text.disabled",
              flexShrink: 0,
            }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {organizationId ? (
              <Link
                component="button"
                variant="body2"
                onClick={() => onOpenOrganization(organizationId)}
                sx={{
                  fontWeight: 600,
                  textAlign: "left",
                  display: "block",
                  lineHeight: 1.4,
                }}
              >
                {clientName}
              </Link>
            ) : (
              <Typography
                variant="body2"
                fontWeight={600}
                color="text.secondary"
                sx={{ lineHeight: 1.4 }}
              >
                {clientName}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary">
              {getDeliveryStatusLabel(task.delivery_result)}
            </Typography>
          </Box>
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            pl: 3,
            pt: 0.25,
            borderTop: "1px dashed",
            borderColor: "divider",
          }}
        >
          <Typography variant="caption" color="text.secondary">
            ID da tarefa
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography
              variant="body2"
              component="span"
              sx={{ fontFamily: "monospace", fontWeight: 600 }}
            >
              {task.id}
            </Typography>
            <Tooltip title={isCopied ? "Copiado!" : "Copiar ID"}>
              <IconButton
                size="small"
                onClick={() => onCopyTaskId(task.id)}
                sx={{ p: 0.5 }}
              >
                {isCopied ? (
                  <CheckIcon sx={{ fontSize: 16, color: "success.main" }} />
                ) : (
                  <ContentCopyIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
};

const RRDeliveredTasks = () => {
  const navigate = useNavigate();
  const [state, setState] = useState(false);
  const [rrTasks, setRrTasks] = useState<any>([]);
  const [activityLogs, setActivityLogs] = useState<any>([]);
  const [projectOrganizationMap, setProjectOrganizationMap] = useState<
    Record<number, string>
  >({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [copiedTaskId, setCopiedTaskId] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const date = new Date();
  date.setHours(0, 0, 0, 0);

  const copyTaskId = useCallback((taskId: number) => {
    navigator.clipboard.writeText(String(taskId));
    setCopiedTaskId(taskId);
    window.setTimeout(() => setCopiedTaskId(null), 2000);
  }, []);

  const openOrganization = useCallback(
    (organizationId: string) => {
      navigate(`/organizations/${organizationId}`);
    },
    [navigate],
  );

  const filterByDate = (row: any) => {
    const taskDate = new Date(row.close_date);
    taskDate.setHours(0, 0, 0, 0);

    return taskDate.valueOf() === date.valueOf() && row.attachments_count > 0;
  };

  const buildProjectOrganizationMap = (organizations: any[]) => {
    const map: Record<number, string> = {};

    organizations.forEach((organization) => {
      organization.runrunit_projects?.forEach((projectId: number | string) => {
        map[Number(projectId)] = organization._id;
      });
    });

    return map;
  };

  const init = async () => {
    setLoading(true);
    setLoadError(false);
    setPage(0);

    try {
      const [queryTasks, queryActivityLogs, organizationsResponse] =
        await Promise.all([
          fetchRRData(
            `/tasks?is_closed=true&limit=200&sort=close_date&sort_dir=desc`,
            {
              method: "GET",
            },
          ),
          fetchData(`/histories/runrunit/tasks/acitivity/logs`, {
            method: "GET",
          }),
          fetchData(`/organizations?limit=9999`, {
            method: "GET",
          }),
        ]);

      setProjectOrganizationMap(
        buildProjectOrganizationMap(organizationsResponse.rows || []),
      );
      setRrTasks(queryTasks.filter(filterByDate));
      setActivityLogs(queryActivityLogs);
    } catch (error) {
      console.log(error);
      setLoadError(true);
    }

    setLoading(false);
  };

  useEffect(() => {
    setRrTasks((currentTasks: any[]) =>
      currentTasks.map((task: any) => ({
        ...task,
        delivery_result: activityLogs.some((row: any) => row.id === task.id),
      })),
    );
  }, [activityLogs]);

  const totalPages = Math.max(1, Math.ceil(rrTasks.length / pageSize));

  const paginatedTasks = useMemo(() => {
    const start = page * pageSize;
    return rrTasks.slice(start, start + pageSize);
  }, [page, rrTasks]);

  useEffect(() => {
    if (page > totalPages - 1) {
      setPage(Math.max(totalPages - 1, 0));
    }
  }, [page, totalPages]);

  const paginationLabel = useMemo(() => {
    if (!rrTasks.length) return "Nenhuma tarefa encontrada";

    const from = page * pageSize + 1;
    const to = Math.min((page + 1) * pageSize, rrTasks.length);

    return `Exibindo ${from}–${to} de ${rrTasks.length} tarefas`;
  }, [page, rrTasks.length]);

  return (
    <>
      <Button
        variant="contained"
        size="small"
        sx={{ flex: 1 }}
        onClick={() => {
          init();
          setState(true);
        }}
      >
        Entregues hoje
      </Button>
      <Drawer
        anchor="right"
        open={state}
        onClose={() => {
          setState(false);
        }}
      >
        <Box
          sx={{
            p: 3,
            pt: 2,
            width: { xs: "100vw", sm: 400 },
            maxWidth: "100vw",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>
              Tarefas entregues hoje
            </Typography>
            <IconButton onClick={() => setState(false)}>
              <CloseIcon />
            </IconButton>
          </Stack>

          <Divider sx={{ my: 2 }} />

          {!loading && !loadError && (
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Box
                  width={8}
                  height={8}
                  borderRadius="50%"
                  sx={{ bgcolor: "success.main" }}
                />
                <Typography variant="caption" color="text.secondary">
                  Notificada
                </Typography>
              </Stack>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Box
                  width={8}
                  height={8}
                  borderRadius="50%"
                  sx={{ bgcolor: "error.main" }}
                />
                <Typography variant="caption" color="text.secondary">
                  Pendente
                </Typography>
              </Stack>
            </Stack>
          )}

          {loading && <LinearProgress sx={{ mb: 2 }} />}

          {loadError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Não foi possível carregar as tarefas. Tente novamente.
            </Alert>
          )}

          {!loading && !loadError && rrTasks.length === 0 && (
            <Alert severity="info">Nenhuma tarefa entregue hoje.</Alert>
          )}

          {!loading && !loadError && rrTasks.length > 0 && (
            <>
              <Stack
                spacing={1.25}
                sx={{ flex: 1, minHeight: 0, overflow: "auto" }}
              >
                {paginatedTasks.map((task: any) => (
                  <DeliveredTaskRow
                    key={task.id}
                    task={task}
                    organizationId={
                      projectOrganizationMap[Number(task.project_id)]
                    }
                    copiedTaskId={copiedTaskId}
                    onCopyTaskId={copyTaskId}
                    onOpenOrganization={openOrganization}
                  />
                ))}
              </Stack>

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1}
                sx={{
                  pt: 2,
                  mt: 1,
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography variant="caption" color="text.secondary" noWrap>
                  {paginationLabel}
                </Typography>
                <Stack direction="row" spacing={0.5} flexShrink={0}>
                  <IconButton
                    size="small"
                    disabled={page === 0}
                    onClick={() => setPage((current) => current - 1)}
                    aria-label="Página anterior"
                  >
                    <ChevronLeftIcon fontSize="small" />
                  </IconButton>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      alignSelf: "center",
                      minWidth: 48,
                      textAlign: "center",
                    }}
                  >
                    {page + 1}/{totalPages}
                  </Typography>
                  <IconButton
                    size="small"
                    disabled={page >= totalPages - 1}
                    onClick={() => setPage((current) => current + 1)}
                    aria-label="Próxima página"
                  >
                    <ChevronRightIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>
            </>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default RRDeliveredTasks;
